import { CTA, PrimaryCategoryData } from 'types';
import React, { useEffect, useRef } from 'react';
import { checkStores, setDataEmTag } from 'utility/utility';

import ErrorBoundaryWithLogger from 'components/common/ErrorBoundary/ErrorBoundary';
import { HomefieldIconSignCancel } from '@dsg-tech/homefield-react-icons';
import useOutsideAlerter from 'hooks/useOutsideAlerter';
import { KEY_CODES } from 'utility/constants';
import MenuColumns from './MenuColumns';

type Props = {
  showSubMenu: boolean;
  desktopNavData: PrimaryCategoryData[];
  changeFocus: (offset?: number) => void;
  index: number;
  closeMenu: () => void;
  firstSubLinkRef: React.RefObject<HTMLAnchorElement | null>;
};

export default function MenuContainer({
  showSubMenu,
  desktopNavData,
  changeFocus,
  index,
  closeMenu,
  firstSubLinkRef,
}: Readonly<Props>) {
  const wrapperRef = useRef(null);
  const handleMenuClose = (e) => {
    if (!e.target.id.includes('desktop-nav-l0')) {
      closeMenu();
    }
  };
  useOutsideAlerter(wrapperRef, handleMenuClose);

  useEffect(() => {
    const pageHeight = document.body.scrollHeight;
    const parentContainer = document.getElementsByClassName(
      'desktop-nav-container',
    )[0] as HTMLElement;

    if (showSubMenu) {
      (
        parentContainer.getElementsByClassName('menu-container')[0] as HTMLElement
      ).style.height = `${pageHeight}px`;
    } else
      (parentContainer.getElementsByClassName('menu-container')[0] as HTMLElement).style.height =
        'unset';
  }, [showSubMenu]);

  const checkForFlyoutImageData = (): boolean => {
    return (
      Array.isArray(desktopNavData) &&
      index >= 0 &&
      index < desktopNavData.length &&
      desktopNavData[index] &&
      Array.isArray(desktopNavData[index].flyoutImage) &&
      desktopNavData[index].flyoutImage.length > 0 &&
      'title' in desktopNavData[index].flyoutImage[0]
    );
  };

  const flyOutImageCTAs = () => {
    return desktopNavData[index].cta && desktopNavData[index].cta.length > 0
      ? desktopNavData[index].cta.map(
          (cta: CTA, i: number) =>
            cta.linkTitle && (
              <ErrorBoundaryWithLogger
                componentId="MenuContainer-flyoutImageCtas"
                key={cta.linkTitle}>
                <a
                  className={`below-image-cta ${i === 0 ? 'hmf-header-bold-s ' : ''}hmf-mb-xs`}
                  href={checkStores(cta.link)}
                  data-em={setDataEmTag(
                    'TopNav',
                    desktopNavData[index].title,
                    'FeaturedColumn',
                    cta.linkTitle,
                  )}>
                  {cta.linkTitle}
                </a>
              </ErrorBoundaryWithLogger>
            ),
        )
      : null;
  };

  const flyOutImage = () => {
    if (!checkForFlyoutImageData()) {
      return null;
    }
    return (
      <div className="desktop-nav-container__menu__column" tabIndex={-1}>
        <div className="flyOutImage-container">
          <img
            className="image"
            src={`https://images.dickssportinggoods.com/marketing/${desktopNavData[index].flyoutImage[0].imageUpload}`}
            alt=""
            loading="lazy"
          />
          <div className="image-text hmf-p-xxs">
            <a
              className="image-text-link hmf-p-s"
              href={checkStores(desktopNavData[index].flyoutImage[0].link)}
              data-em={setDataEmTag(
                'TopNav',
                desktopNavData[index].title,
                'Creative',
                desktopNavData[index].flyoutImage[0].title,
              )}>
              <p className="title hmf-header-bold-m hmf-pb-xs">
                {desktopNavData[index].flyoutImage[0].title}
              </p>
              <p className="cta hmf-subheader-bold-m">
                {desktopNavData[index].flyoutImage[0].linkTitle}
              </p>
            </a>
          </div>
        </div>
        <div className="below-image-cta-container hmf-subheader-m hmf-p-s">
          {desktopNavData[index].cta && flyOutImageCTAs()}
        </div>
      </div>
    );
  };

  return (
    <div className="menu-container">
      {showSubMenu && <div className="background-overlay" />}
      <div
        role="menu"
        className={`desktop-nav-container__menu hmf-pt-m hmf-pb-s hmf-pl-m ${
          showSubMenu ? 'hmf-display-flex' : 'hmf-display-none'
        }`}
        ref={wrapperRef}
        tabIndex={0}>
        <MenuColumns
          desktopNavData={desktopNavData}
          index={index}
          changeFocus={changeFocus}
          firstSubLinkRef={firstSubLinkRef}
        />
        {flyOutImage()}
        <button
          className="close-button"
          type="button"
          aria-label="Close Menu"
          onClick={() => closeMenu()}
          onKeyDown={(event) => {
            if (event.key === KEY_CODES.SPACE) {
              closeMenu();
            }
          }}
          tabIndex={0}>
          <HomefieldIconSignCancel
            className="close-button__icon hmf-mt-xs"
            height="22px"
            width="22px"
          />
          <span className="close-button__text hmf-body-bold-m hmf-m-xxs">Close</span>
        </button>
      </div>
    </div>
  );
}
